import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";
import { ChevronRight, ChevronsDown } from "lucide-react";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useRequest } from "@/hooks/use-request";

const notificationFormSchema = z.object({
	name: z.string().min(1, { message: "이름을 입력해주세요." }),
	phone: z
		.string()
		.min(1, { message: "전화번호를 입력해주세요." })
		.regex(/^[0-9-]+$/, { message: "올바른 전화번호 형식이 아닙니다." }),
	hospital_name: z.string().min(1, { message: "병원명을 입력해주세요." }),
	privacyAgreed: z.boolean().refine((val) => val === true, {
		message: "개인정보 처리방침에 동의해주세요.",
	}),
});

export const Route = createFileRoute("/submit")({
	component: RouteComponent,
});

function RouteComponent() {
	const { post } = useRequest({ skipAuth: true });
	const firstImageRef = useRef<HTMLElement>(null);

	const notificationForm = useForm<z.infer<typeof notificationFormSchema>>({
		resolver: zodResolver(notificationFormSchema),
		defaultValues: {
			name: "",
			phone: "",
			hospital_name: "",
			privacyAgreed: false,
		},
	});

	// Vimeo 플레이어 스크립트 로드
	useEffect(() => {
		const script = document.createElement("script");
		script.src = "https://player.vimeo.com/api/player.js";
		script.async = true;
		document.body.appendChild(script);

		return () => {
			document.body.removeChild(script);
		};
	}, []);

	const handleNotificationSubmit = async (
		values: z.infer<typeof notificationFormSchema>,
	) => {
		try {
			await post<{ success: boolean; data: Record<string, never> }>(
				"promotion/apply",
				{
					name: values.name,
					phone: values.phone,
					hospital_name: values.hospital_name,
				},
			);

			toast.success("알림톡 신청이 완료되었습니다.");
			notificationForm.reset();
		} catch (error) {
			// 에러는 APIClient의 handleError에서 이미 처리됨
			console.error("알림톡 신청 실패:", error);
		}
	};

	const handleScrollToFirstImage = () => {
		firstImageRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	return (
		<div className="w-full">
			<div className="mx-auto max-w-md">
				{/* Section 0: 동영상 */}
				<section className="bg-white relative">
					<div style={{ padding: "177.78% 0 0 0", position: "relative" }}>
						<iframe
							src="https://player.vimeo.com/video/1143907488?badge=0&autopause=0&player_id=0&app_id=58479"
							frameBorder="0"
							allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
							referrerPolicy="strict-origin-when-cross-origin"
							style={{
								position: "absolute",
								top: 0,
								left: 0,
								width: "100%",
								height: "100%",
							}}
							title="Doxmeet_AD_Shorts_환자설명자료"
						/>
						<button
							type="button"
							onClick={handleScrollToFirstImage}
							className="absolute bottom-0 left-1/2 transform -translate-x-1/2 bg-transparent border-none p-2 cursor-pointer hover:opacity-80 transition-opacity"
							aria-label="첫 번째 이미지로 스크롤"
						>
							<ChevronsDown className="size-6 text-white/80 animate-bounce drop-shadow-[0_1px_3px_rgba(0,0,0,1)]" />
						</button>
					</div>
				</section>

				{/* Section 1: 서비스 소개 */}
				<section ref={firstImageRef} className="bg-white">
					<img
						src="/images/submit/01.jpg"
						alt="닥스밋 서비스 소개"
						className="w-full h-auto"
					/>
				</section>

				{/* Section 2: 자료가 없을 때 알림톡 신청 폼 */}
				<section className="bg-white py-12 px-4">
					<div className="mx-auto">
						<h2 className="text-2xl md:text-3xl font-bold text-black mb-6 px-8">
							자료가 지금
							<br />
							없으시다구요?
						</h2>

						<Card className="bg-gray-50 p-6 md:p-8">
							<p className="text-xl md:text-2xl font-bold text-black mb-6">
								언제라도 올릴 수 있게
								<br />
								알림톡 보내드릴게요!
							</p>

							<Form {...notificationForm}>
								<form
									onSubmit={notificationForm.handleSubmit(
										handleNotificationSubmit,
									)}
									className="space-y-6"
								>
									<FormField
										control={notificationForm.control}
										name="name"
										render={({ field }) => (
											<FormItem>
												<FormLabel className="text-base font-medium gap-0.5">
													이름<sup className="text-red-500">*</sup>
												</FormLabel>
												<FormControl>
													<Input
														placeholder="이름을 입력해주세요"
														className="bg-white"
														{...field}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={notificationForm.control}
										name="phone"
										render={({ field }) => (
											<FormItem>
												<FormLabel className="text-base font-medium gap-0.5">
													전화번호<sup className="text-red-500">*</sup>
												</FormLabel>
												<FormControl>
													<Input
														placeholder="전화번호를 입력해주세요"
														className="bg-white"
														{...field}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={notificationForm.control}
										name="hospital_name"
										render={({ field }) => (
											<FormItem>
												<FormLabel className="text-base font-medium gap-0.5">
													병원명<sup className="text-red-500">*</sup>
												</FormLabel>
												<FormControl>
													<Input
														placeholder="병원명을 입력해주세요"
														className="bg-white"
														{...field}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={notificationForm.control}
										name="privacyAgreed"
										render={({ field }) => (
											<FormItem className="flex flex-row items-center space-y-0">
												<FormControl>
													<Checkbox
														checked={field.value}
														onCheckedChange={field.onChange}
													/>
												</FormControl>
												<div className="space-y-1 leading-none">
													<FormLabel className="text-sm font-normal cursor-pointer">
														개인정보 처리방침에 동의합니다.
													</FormLabel>
												</div>
											</FormItem>
										)}
									/>

									<Button
										type="submit"
										className="w-full bg-blue-600 hover:bg-blue-700 text-white text-base font-semibold py-6 flex items-center justify-center gap-2"
									>
										알림톡 보내두기 <ChevronRight className="size-5" />
									</Button>
								</form>
							</Form>
						</Card>
					</div>
				</section>

				{/* Section 3: 파일 업로드 섹션 */}
				<section className="bg-white">
					<a
						href="https://doxmeet.synology.me:5001/sharing/9ib9S0bLl"
						target="_blank"
						rel="noopener noreferrer"
						className="block cursor-pointer hover:opacity-90 transition-opacity"
					>
						<img
							src="/images/submit/03.jpg"
							alt="자료가 있다면?"
							className="w-full h-auto"
						/>
					</a>
				</section>

				{/* Section 4: 유의사항 */}
				<section className="bg-gray-800">
					<img
						src="/images/submit/02.jpg"
						alt="유의사항"
						className="w-full h-auto"
					/>
				</section>

				{/* Section 5: DOXMEET 로고와 바로가기 */}
				<section className="bg-white py-12 px-4">
					<a
						href="https://www.doxmeet.com"
						target="_blank"
						rel="noopener noreferrer"
						className="mx-auto flex items-center justify-center gap-4 hover:opacity-80 transition-opacity"
					>
						<img src="/images/dxmt_logo.png" alt="DOXMEET" className="h-8" />
						<span className="text-black font-semibold text-lg">바로가기</span>
					</a>
				</section>
			</div>
		</div>
	);
}
